/**
 * The retired `/blog` posts and the live page each one now redirects to.
 *
 * Keys are the exact Builder `blog-article` slugs. {@link resolveLegacyRedirect}
 * matches them in lowercase (one live slug has a capital I).
 *
 * Every target must be a page that answers 200. Add a post here in the same
 * change that points its old address at the target.
 *
 * Aliases: blog 301 map, retired blog posts, blog slug redirects
 */

/** Where the old blog index, members blog and author pages now start. */
export const ARTICLES_INDEX_PATH = "/articles";

/** Posts that moved into an article, coach guide or hub page. */
export const BLOG_POST_REDIRECTS: Readonly<Record<string, string>> = {
  // Same topic as a live article or coach guide.
  "what-is-coaching": "/articles/what-is-coaching",
  "what-is-coaching-main-features-and-advices": "/articles/what-is-coaching",
  "whats-the-right-price-for-coaching": "/articles/how-much-does-coaching-cost",
  "what-factors-affect-the-cost-of-hiring-a-life-coach":
    "/articles/how-much-does-coaching-cost",
  "life-coach-vs-therapist-what-is-the-difference":
    "/articles/life-coach-vs-therapist",
  "can-a-therapist-be-a-life-coach": "/articles/life-coach-vs-therapist",
  "why-do-therapists-hate-life-coaches": "/articles/life-coach-vs-therapist",
  "what-is-ceo-coaching": "/articles/what-does-a-career-coach-do",
  "step-by-step-guide-to-finding-the-right-career-coach":
    "/articles/what-does-a-career-coach-do",
  "how-to-start-a-career-coaching-business":
    "/coaching-business/coaching-business-roadmap",
  "how-to-use-ai-in-coaching": "/coaching-business/ai-for-coaches",
  "the-most-profitable-coaching-niches":
    "/coaching-business/most-profitable-coaching-niches",
  "best-patient-scheduling-software-for-coaches":
    "/coaching-business/coaching-tech-stack",
  "how-to-find-your-coaching-niche":
    "/coaching-business/how-to-choose-coaching-niche",
  // Named-people lists and product how-tos, sent to the live hub for the intent.
  "top-15-certification-programs-in-life-coaching": "/training-programs",
  "best-coaches-in-the-world-for-life-and-business": "/coaches",
  "top-10-career-coaches-on-linkedIn":
    "/coaching-for/career-crossroads/coaches",
  "9-dating-coaches-you-should-follow-on-instagram":
    "/coaching-for/relationships-at-a-breaking-point/coaches",
  "top-executive-coaching-models": "/frameworks",
  "how-to-find-a-coach-at-dreamcoachmatch": "/how-it-works",
  // Posts imported into /articles under the same slug.
  "how-to-manage-stress": "/articles/how-to-manage-stress",
  "career-coaching-vs-career-counselling":
    "/articles/career-coaching-vs-career-counselling",
  "group-life-coaching-what-is-it": "/articles/group-life-coaching-what-is-it",
  "best-relationship-coaching-techniques":
    "/articles/best-relationship-coaching-techniques",
  "what-is-one-on-one-coaching-and-how-it-works":
    "/articles/what-is-one-on-one-coaching-and-how-it-works",
  "coaching-during-crisis-guiding-through-turbulence-with-clarity-and-resilience":
    "/articles/coaching-during-crisis-guiding-through-turbulence-with-clarity-and-resilience",
  "what-is-executive-coaching-and-who-needs-it":
    "/articles/what-is-executive-coaching-and-who-needs-it",
  "how-personal-coaching-can-boost-your-self-development":
    "/articles/how-personal-coaching-can-boost-your-self-development",
  "what-to-expect-from-working-with-a-relationship-coach":
    "/articles/what-to-expect-from-working-with-a-relationship-coach",
  "what-to-expect-from-your-first-coaching-session":
    "/articles/what-to-expect-from-your-first-coaching-session",
  "how-family-coaches-help-improve-family-dynamics":
    "/articles/how-family-coaches-help-improve-family-dynamics",
  "how-to-choose-a-life-coach": "/articles/how-to-choose-a-life-coach",
  "common-topics-to-explore-in-life-coaching":
    "/articles/common-topics-to-explore-in-life-coaching",
  "career-and-business-coach-main-differences":
    "/articles/career-and-business-coach-main-differences",
  "how-does-mindfulness-coaching-help-to-reduce-stress":
    "/articles/how-does-mindfulness-coaching-help-to-reduce-stress",
  "life-coach-vs-health-coach": "/articles/life-coach-vs-health-coach",
  "burnout-coaching-keep-work-life-balance-as-a-pro":
    "/articles/burnout-coaching-keep-work-life-balance-as-a-pro",
  "how-to-measure-coaching-effectiveness":
    "/articles/how-to-measure-coaching-effectiveness",
  // Coach-facing posts imported as coach guides.
  "how-to-become-a-business-coach-build-a-coaching-career":
    "/coaching-business/how-to-become-a-business-coach-build-a-coaching-career",
  "how-to-structure-a-coaching-session":
    "/coaching-business/how-to-structure-a-coaching-session",
  "17-best-mindset-coaching-tools-for-personal-growth":
    "/coaching-business/17-best-mindset-coaching-tools-for-personal-growth",
  // Posts folded into an imported page.
  "which-coach-is-right-for-me": "/articles/how-to-choose-a-life-coach",
  "how-to-hire-a-coach": "/articles/how-to-choose-a-life-coach",
  "how-to-choose-a-business-coach": "/articles/how-to-choose-a-life-coach",
  "start-a-successful-coaching-career":
    "/coaching-business/how-to-become-a-business-coach-build-a-coaching-career",
  "group-coaching-programs-pros-and-cons":
    "/articles/group-life-coaching-what-is-it",
  "how-to-become-a-certified-executive-coach":
    "/articles/what-is-executive-coaching-and-who-needs-it",
};

/** The three blog author pages; each now starts at the article index. */
const BLOG_AUTHOR_SLUGS: ReadonlySet<string> = new Set([
  "aiseek",
  "anna",
  "yuri-minski",
]);

/** The article index for a retired blog author page, or `null` for any other slug. */
export function blogAuthorRedirectTarget(slug: string): string | null {
  return BLOG_AUTHOR_SLUGS.has(slug.toLowerCase()) ? ARTICLES_INDEX_PATH : null;
}

const REDIRECT_BY_LOWERCASE_SLUG: ReadonlyMap<string, string> = new Map(
  Object.entries(BLOG_POST_REDIRECTS).map(([slug, to]) => [
    slug.toLowerCase(),
    to,
  ]),
);

/** The page a retired blog post now lives at, or `null` for any other slug. */
export function blogPostRedirectTarget(slug: string): string | null {
  return REDIRECT_BY_LOWERCASE_SLUG.get(slug.toLowerCase()) ?? null;
}
